import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Nav, Row } from 'react-bootstrap';
import { listInvoices, verifyInvoice, downloadInvoicePdf, getInvoiceRegisterSummary } from '../api/financeApi';
import useFinanceQuery from '../useFinanceQuery';
import FinanceListPage from '../components/FinanceListPage';
import { useFinance } from '../financeContext';
import { money } from '../format';

// manual_vs_automatic_invoicing.md — two views:
//   register  = AUTOMATIC / PLATFORM_SUBSCRIPTION invoices (a GST document trail for membership
//               money already counted as MRR — NOT re-summed as revenue)
//   queue     = OWNER_BUSINESS invoices an owner has marked paid; finance verifies, which is what
//               lets that amount show as the owner's own revenue.

function VerifyModal({ row, onClose, onDone }) {
  const [decision, setDecision] = useState('VERIFIED');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setBusy(true); setErr('');
    try { await verifyInvoice(row.id, { decision, note }); onDone(); }
    catch (e) { setErr(e?.response?.data?.msg || 'Unable to record that.'); setBusy(false); }
  };
  return (
    <Modal show onHide={onClose}>
      <Modal.Header closeButton><Modal.Title as="h6" className="mb-0">Verify payment — {row.invoiceNumber}</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <Alert variant="danger">{err}</Alert>}
        <p className="text-muted small">
          {row.ownerName || row.ownerEmail} recorded a {row.payment?.method?.replace(/_/g, ' ').toLowerCase()} payment
          {row.payment?.reference ? ` (ref ${row.payment.reference})` : ''} of {money(row.payment?.amount || row.totalAmount)}.
          Verifying lets this amount count toward the owner&apos;s revenue.
        </p>
        <Form.Group className="mb-2">
          <Form.Select size="sm" value={decision} onChange={(e) => setDecision(e.target.value)}>
            <option value="VERIFIED">Verified — payment confirmed</option>
            <option value="DISPUTED">Disputed — payment not found</option>
          </Form.Select>
        </Form.Group>
        <Form.Control size="sm" as="textarea" rows={2} placeholder="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button variant={decision === 'DISPUTED' ? 'danger' : 'success'} disabled={busy} onClick={submit}>
          {busy ? 'Saving…' : decision === 'DISPUTED' ? 'Mark disputed' : 'Verify'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function RegisterSummary() {
  const [s, setS] = useState(null);
  useEffect(() => { getInvoiceRegisterSummary().then(setS).catch(() => {}); }, []);
  if (!s) return null;
  return (
    <Row className="g-2 mb-3">
      <Col xs={6} md={3}><Card body className="text-center"><div className="fw-bold">{s.platformInvoices.count}</div><div className="small text-muted">Subscription invoices</div></Card></Col>
      <Col xs={6} md={3}><Card body className="text-center"><div className="fw-bold">{money(s.platformInvoices.taxableTotal)}</div><div className="small text-muted">Taxable value</div></Card></Col>
      <Col xs={6} md={3}><Card body className="text-center"><div className="fw-bold">{money(s.platformInvoices.gstTotal)}</div><div className="small text-muted">GST</div></Card></Col>
      <Col xs={6} md={3}><Card body className="text-center"><div className="fw-bold">{s.ownerInvoicesAwaitingVerification}</div><div className="small text-muted">Owner invoices to verify</div></Card></Col>
      <Col xs={12}><div className="small text-muted">{s.platformInvoices.note}</div></Col>
    </Row>
  );
}

// referred - corporate transport receivables: what companies still owe operators on issued invoices.
function ReceivablesSummary() {
  const [s, setS] = useState(null);
  useEffect(() => { getInvoiceRegisterSummary().then((r) => setS(r?.corporateTransport)).catch(() => {}); }, []);
  if (!s) return null;
  return (
    <Row className="g-2 mb-3">
      <Col xs={6} md={3}><Card body className="text-center"><div className="fw-bold">{money(s.invoiced)}</div><div className="small text-muted">Invoiced (issued)</div></Card></Col>
      <Col xs={6} md={3}><Card body className="text-center"><div className="fw-bold text-success">{money(s.received)}</div><div className="small text-muted">Received</div></Card></Col>
      <Col xs={6} md={3}><Card body className="text-center"><div className={`fw-bold ${s.due ? 'text-warning' : ''}`}>{money(s.due)}</div><div className="small text-muted">Outstanding</div></Card></Col>
      <Col xs={6} md={3}><Card body className="text-center"><div className={`fw-bold ${s.overdueCount ? 'text-danger' : ''}`}>{s.overdueCount}</div><div className="small text-muted">Overdue invoices</div></Card></Col>
    </Row>
  );
}

export default function PlatformInvoicesPage() {
  const { canApproveFinance } = useFinance();
  const [view, setView] = useState('register');
  const [verifyTarget, setVerifyTarget] = useState(null);

  const registerQ = useFinanceQuery(
    (params) => listInvoices({ source: 'PLATFORM_SUBSCRIPTION', ...params }),
    { filterKeys: ['dateFrom', 'dateTo', 'ownerId'] }
  );
  const queueQ = useFinanceQuery(
    (params) => listInvoices({ source: 'OWNER_BUSINESS', paymentStatus: 'PAID', verified: 'false', ...params }),
    { filterKeys: ['dateFrom', 'dateTo', 'ownerId'] }
  );
  const receivablesQ = useFinanceQuery(
    (params) => listInvoices({ source: 'CORPORATE_TRANSPORT', ...params }),
    { filterKeys: ['dateFrom', 'dateTo', 'ownerId'] }
  );
  const dueReceivablesQ = useFinanceQuery(
    (params) => listInvoices({ source: 'CORPORATE_TRANSPORT', dueOnly: 'true', ...params }),
    { filterKeys: ['dateFrom', 'dateTo', 'ownerId'] }
  );
  const q = view === 'register' ? registerQ : view === 'receivables' ? receivablesQ : view === 'due' ? dueReceivablesQ : queueQ;

  const commonCols = [
    { key: 'invoiceNumber', header: 'Invoice #', cardHeader: true, cell: (r) => <span className="font-monospace">{r.invoiceNumber}</span> },
    { key: 'date', header: 'Date', cell: (r) => new Date(r.invoiceDate).toLocaleDateString('en-IN') },
    { key: 'owner', header: 'Owner', cell: (r) => <span>{r.ownerName || r.ownerId}<div className="small text-muted">{r.ownerEmail}</div></span> },
    { key: 'amount', header: 'Amount', cell: (r) => <span>{money(r.totalAmount)}<div className="small text-muted">GST {money(r.gstTotal)}</div></span> }
  ];

  const registerCols = [
    ...commonCols,
    { key: 'plan', header: 'Plan', cell: (r) => r.relatedPlanCode || '—' },
    { key: 'pdf', header: '', cell: (r) => <Button size="sm" variant="outline-secondary" onClick={() => downloadInvoicePdf(r.id, r.invoiceNumber)}>PDF</Button> }
  ];

  const queueCols = [
    ...commonCols,
    { key: 'client', header: 'Client', cell: (r) => r.customerName || '—' },
    { key: 'paid', header: 'Recorded payment', cell: (r) => r.payment
      ? <span>{r.payment.method?.replace(/_/g, ' ').toLowerCase()}<div className="small text-muted">ref {r.payment.reference}</div></span>
      : '—' },
    { key: 'state', header: 'State', cell: (r) => r.financeReview?.decision === 'DISPUTED'
      ? <Badge bg="danger">Disputed</Badge>
      : <Badge bg="warning" text="dark">Awaiting</Badge> }
  ];

  const receivablesCols = [
    ...commonCols,
    { key: 'period', header: 'Period', cell: (r) => r.billingPeriod || '—' },
    { key: 'client', header: 'Company', cell: (r) => r.customerName || '—' },
    { key: 'out', header: 'Outstanding', cell: (r) => (r.outstanding > 0
      ? <Badge bg={r.overdue ? 'danger' : 'warning'} text={r.overdue ? undefined : 'dark'}>{money(r.outstanding)}{r.overdue ? ' overdue' : ''}</Badge>
      : <Badge bg="success">settled</Badge>) },
    { key: 'due', header: 'Due by', cell: (r) => (r.dueDate ? new Date(r.dueDate).toLocaleDateString('en-IN') : '—') },
    { key: 'stage', header: 'Stage', cell: (r) => <Badge bg={r.lifecycle === 'PAID' ? 'success' : r.lifecycle === 'DRAFT' ? 'secondary' : 'primary'}>{r.lifecycle || 'ISSUED'}</Badge> },
    { key: 'pdf', header: '', cell: (r) => <Button size="sm" variant="outline-secondary" onClick={() => downloadInvoicePdf(r.id, r.invoiceNumber)}>PDF</Button> }
  ];

  const rowActions = view !== 'queue'
    ? undefined
    : (!canApproveFinance ? undefined : (r) => (
      <div className="d-flex gap-1 flex-wrap">
        <Button size="sm" variant="outline-secondary" onClick={() => downloadInvoicePdf(r.id, r.invoiceNumber)}>PDF</Button>
        <Button size="sm" variant="success" onClick={() => setVerifyTarget(r)}>Verify / dispute</Button>
      </div>
    ));

  return (
    <>
      <h5 className="mb-2">Platform invoices</h5>
      <Nav variant="pills" className="mb-3" activeKey={view} onSelect={(k) => setView(k)}>
        <Nav.Item><Nav.Link eventKey="register">Subscription invoices</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="queue">Owner invoices to verify</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="due">Transport payments due</Nav.Link></Nav.Item>
        <Nav.Item><Nav.Link eventKey="receivables">Transport receivables</Nav.Link></Nav.Item>
      </Nav>

      {view === 'register' && <RegisterSummary />}
      {view === 'receivables' && <ReceivablesSummary />}

      <FinanceListPage
        title={view === 'register' ? 'Membership tax invoices'
          : view === 'receivables' ? 'Corporate transport invoices'
            : view === 'due' ? 'Corporate transport payments due'
            : 'Owner-business invoices awaiting verification'}
        subtitle={view === 'register'
          ? 'GST document trail for membership payments. These are already counted as MRR — this page is a reconciliation view, not a separate revenue figure.'
          : view === 'receivables' || view === 'due'
            ? 'The monthly invoice each cab operator raises to a corporate client for employee transport, and what the client still owes. The operator records the payment; there is no verification step.'
            : 'An owner has recorded that their client paid. Verifying confirms it counts toward that owner’s own revenue; it never affects any platform figure.'}
        q={q}
        columns={view === 'register' ? registerCols : view === 'receivables' || view === 'due' ? receivablesCols : queueCols}
        rowKey={(r) => r.id}
        rowActions={rowActions}
        filterFields={['date', 'owner']}
        searchPlaceholder="Search invoice #, client or owner"
        emptyText={view === 'register' ? 'No subscription invoices yet.'
          : view === 'receivables' ? 'No corporate transport invoices yet.'
            : view === 'due' ? 'No corporate transport payments are due.'
            : 'Nothing awaiting verification.'}
      />

      {verifyTarget && (
        <VerifyModal row={verifyTarget} onClose={() => setVerifyTarget(null)}
          onDone={() => { setVerifyTarget(null); q.reload(); }} />
      )}
    </>
  );
}
